import { ChangeEvent, FormEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ExternalLink, Loader2, UserRound } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { ContactKind, Me, PendingChange, SocialConnection } from '../domain/account';
import ContactPointsSection from '../components/ContactPointsSection';
import DeleteAccountDialog from '../components/DeleteAccountDialog';
import { PRIVACY_URL, SUPPORT_URL, TERMS_URL } from '../domain/legal';
import { useRepositories } from '../hooks/useRepositories';
import { avatarColorClasses } from '../utils/avatarColors';
import { getApiErrorMessage } from '../utils/errors';
import { fullName } from '../utils/people';

const PROVIDER_NAMES: Record<string, string> = {
  google: 'Google',
  apple: 'Apple',
};

/**
 * Mirrors the server's avatar rules so a bad file is refused before it's
 * uploaded, using the same wording the server would have replied with — a
 * duplicated rule is worth it here only because the alternative is watching a
 * 10 MB upload finish just to be told it was too big.
 */
const AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const AVATAR_MAX_BYTES = 10 * 1024 * 1024;
const AVATAR_TYPE_ERROR = 'Unsupported file type. Use JPEG, PNG, or WebP.';
const AVATAR_SIZE_ERROR = 'File too large. Maximum size is 10 MB.';

const errorMessage = getApiErrorMessage;

function formatConnectedDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function initials(profile: Me): string {
  const fromName = `${profile.firstName.trim()}${profile.lastName.trim()}`;
  if (fromName) {
    return `${profile.firstName.trim()[0] ?? ''}${profile.lastName.trim()[0] ?? ''}`.toUpperCase();
  }
  return profile.username.slice(0, 1).toUpperCase();
}

// ── Avatar ────────────────────────────────────────────────────────────────

interface AvatarSectionProps {
  profile: Me;
  /**
   * Reports just the new avatar rather than a whole profile: an upload is a
   * slow request, and writing back a profile captured before it started would
   * undo anything that landed while it was in flight.
   */
  onAvatarChange: (avatarUrl: string | null) => void;
}

function AvatarSection({ profile, onAvatarChange }: AvatarSectionProps) {
  const { account } = useRepositories();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Clear the input either way, so re-picking the same file still fires.
    e.target.value = '';
    if (!file) return;

    if (!AVATAR_TYPES.includes(file.type)) {
      setError(AVATAR_TYPE_ERROR);
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setError(AVATAR_SIZE_ERROR);
      return;
    }

    setBusy(true);
    try {
      const avatarUrl = await account.uploadAvatar(file);
      // Every upload gets a fresh blob path, so the new URL differs from the
      // old one even for identical bytes — no cache-busting needed.
      onAvatarChange(avatarUrl);
      setError(null);
      toast('Photo updated.');
    } catch (err) {
      setError(errorMessage(err, 'Failed to upload photo'));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      await account.removeAvatar();
      onAvatarChange(null);
      setError(null);
      toast('Photo removed.');
    } catch (err) {
      setError(errorMessage(err, 'Failed to remove photo'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-5">
      {profile.avatarUrl ? (
        <img
          src={profile.avatarUrl}
          alt="You"
          className="size-20 shrink-0 rounded-full object-cover"
        />
      ) : (
        <div
          className={`flex size-20 shrink-0 items-center justify-center rounded-full text-xl font-medium ${avatarColorClasses(fullName(profile))}`}
          aria-hidden="true"
        >
          {initials(profile) || <UserRound className="size-8" />}
        </div>
      )}

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          {/* The label is the button: it opens the picker on click and gives
              the visually hidden input an accessible name. */}
          <Label
            htmlFor="avatar-file"
            className={buttonVariants({ variant: 'outline', size: 'sm' })}
          >
            {profile.avatarUrl ? 'Replace photo' : 'Upload photo'}
          </Label>
          <input
            id="avatar-file"
            type="file"
            accept={AVATAR_TYPES.join(',')}
            className="sr-only"
            disabled={busy}
            onChange={handleFile}
          />
          {profile.avatarUrl && (
            <Button variant="ghost" size="sm" onClick={handleRemove} disabled={busy}>
              Remove photo
            </Button>
          )}
          {busy && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
        </div>
        <p className="text-xs text-muted-foreground">JPEG, PNG, or WebP. Up to 10 MB.</p>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
}

// ── Name ──────────────────────────────────────────────────────────────────

interface NameFormProps {
  profile: Me;
  onProfile: (profile: Me) => void;
}

function NameForm({ profile, onProfile }: NameFormProps) {
  const { account } = useRepositories();
  const [firstName, setFirstName] = useState(profile.firstName);
  const [lastName, setLastName] = useState(profile.lastName);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      // No client-side blank check: the server owns that rule and its reply
      // names the offending field, so duplicating it here could only drift.
      const updated = await account.updateName(firstName, lastName);
      onProfile(updated);
      setError(null);
      toast('Name updated.');
    } catch (err) {
      setError(errorMessage(err, 'Failed to save your name'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="first-name">First name</Label>
          <Input
            id="first-name"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="last-name">Last name</Label>
          <Input
            id="last-name"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>
      </div>
      <Button type="submit" disabled={saving}>
        Save name
      </Button>
    </form>
  );
}

// ── Connected accounts / password (unchanged behaviour) ───────────────────

interface ConnectedAccountsSectionProps {
  connections: SocialConnection[];
  loaded: boolean;
  loadFailed: boolean;
}

function ConnectedAccountsSection({
  connections,
  loaded,
  loadFailed,
}: ConnectedAccountsSectionProps) {
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Connected accounts</CardTitle>
      </CardHeader>
      <CardContent>
        {!loaded ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : loadFailed ? (
          <p className="text-sm text-muted-foreground">
            Couldn’t load your connected accounts. Refresh to try again.
          </p>
        ) : connections.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No social accounts connected. You sign in with your username and password.
          </p>
        ) : (
          <ul className="space-y-4">
            {connections.map((connection) => (
              <li key={`${connection.provider}-${connection.connectedAt}`}>
                <div className="font-medium">
                  {PROVIDER_NAMES[connection.provider] ?? connection.provider}
                </div>
                <div className="text-sm text-muted-foreground">
                  {connection.email ?? 'Email not available'}
                </div>
                <div className="text-sm text-muted-foreground">
                  Connected {formatConnectedDate(connection.connectedAt)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

interface PasswordFormProps {
  hasPassword: boolean;
  onSuccess: () => void;
}

function PasswordForm({ hasPassword, onSuccess }: PasswordFormProps) {
  const { account } = useRepositories();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const clearFields = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      // Keep the fields so the user can fix the mismatch in place.
      setFormError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await account.changePassword({
        ...(hasPassword ? { currentPassword } : {}),
        newPassword,
        confirmNewPassword,
      });
      setFormError(null);
      // Clear only on success — on a validation error the fields are left
      // intact so the user can correct a typo without re-entering everything.
      clearFields();
      toast(
        hasPassword
          ? 'Password updated successfully.'
          : 'Password added — you can now log in with your username and password.',
      );
      onSuccess();
    } catch (err) {
      setFormError(errorMessage(err, 'Failed to update password'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>{hasPassword ? 'Change password' : 'Add password'}</CardTitle>
      </CardHeader>
      <CardContent>
        {!hasPassword && (
          <p className="mb-4 text-sm text-muted-foreground">
            Your account uses social sign-in. Add a password to also log in with your username.
          </p>
        )}

        {formError && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {hasPassword && (
            <div className="space-y-2">
              <Label htmlFor="current-password">Current password</Label>
              <Input
                id="current-password"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="new-password">New password</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-new-password">Confirm new password</Label>
            <Input
              id="confirm-new-password"
              type="password"
              autoComplete="new-password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              required
            />
          </div>
          <Button type="submit" disabled={submitting}>
            {hasPassword ? 'Change password' : 'Add password'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

export function AccountSettingsPage() {
  const { account } = useRepositories();
  const [profile, setProfile] = useState<Me | null>(null);
  const [connections, setConnections] = useState<SocialConnection[]>([]);
  const [connectionsLoaded, setConnectionsLoaded] = useState(false);
  const [connectionsFailed, setConnectionsFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    try {
      const data = await account.me();
      setProfile(data);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Failed to load account settings'));
    } finally {
      setLoading(false);
    }
  }, [account]);

  useEffect(() => {
    fetchProfile();
    // Connections never change from this page, so they're fetched once
    // alongside the initial profile load rather than on every re-fetch.
    // A failure is tracked separately so it reads as "couldn't load"
    // rather than masquerading as a genuine "no connections" empty state.
    account
      .socialConnections()
      .then(setConnections)
      .catch(() => setConnectionsFailed(true))
      .finally(() => setConnectionsLoaded(true));
  }, [account, fetchProfile]);

  // Avatar and pending-change callbacks both merge into whatever the profile
  // is when they land, not what it was when the request went out — these
  // requests overlap in practice and the last one home must not undo the rest.
  const handleAvatar = useCallback((avatarUrl: string | null) => {
    setProfile((current) => (current ? { ...current, avatarUrl } : current));
  }, []);

  // Starting or resending a change only returns the challenge, so it's folded
  // into the profile the page already holds rather than costing a second GET.
  const handlePending = useCallback((kind: ContactKind, pending: PendingChange) => {
    setProfile((current) =>
      current
        ? {
            ...current,
            [kind === 'email' ? 'pendingEmailChange' : 'pendingPhoneChange']: pending,
          }
        : current,
    );
  }, []);
  const [deleting, setDeleting] = useState(false);

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl p-4">
        <div className="text-muted-foreground">Loading…</div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl p-4">
      <h1 className="mb-4 text-2xl font-semibold">Account settings</h1>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {profile && (
        <>
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <AvatarSection profile={profile} onAvatarChange={handleAvatar} />
              <NameForm
                // Remount on a username change is impossible, but a fresh
                // profile from the server should reseed the inputs.
                key={`${profile.firstName}|${profile.lastName}`}
                profile={profile}
                onProfile={setProfile}
              />
              <div>
                <div className="text-sm text-muted-foreground">Username</div>
                <div className="font-medium">{profile.username}</div>
              </div>
            </CardContent>
          </Card>

          <Card className="mb-6" role="region" aria-labelledby="audience-heading">
            <CardHeader>
              <CardTitle id="audience-heading">Who you see</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <Link to="/settings/connections" className="font-medium text-primary hover:underline">
                Connections
              </Link>
              <Link to="/settings/muted" className="font-medium text-primary hover:underline">
                Muted collections
              </Link>
              <Link to="/settings/blocked" className="font-medium text-primary hover:underline">
                Blocked people
              </Link>
              <Link
                to="/settings/notifications"
                className="font-medium text-primary hover:underline"
              >
                Notifications
              </Link>
            </CardContent>
          </Card>

          <ContactPointsSection
            profile={profile}
            onProfile={setProfile}
            onPending={handlePending}
            onRefresh={fetchProfile}
          />

          <PasswordForm hasPassword={profile.hasUsablePassword} onSuccess={fetchProfile} />

          <ConnectedAccountsSection
            connections={connections}
            loaded={connectionsLoaded}
            loadFailed={connectionsFailed}
          />

          <Card className="mb-6" role="region" aria-labelledby="about-heading">
            <CardHeader>
              <CardTitle id="about-heading">About</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              {LEGAL_LINKS.map(({ label, href }) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center gap-1.5 self-start font-medium text-primary hover:underline"
                >
                  {label}
                  <ExternalLink className="size-3.5" aria-hidden="true" />
                </a>
              ))}
            </CardContent>
          </Card>

          <Card className="mb-6" role="region" aria-labelledby="delete-account-heading">
            <CardHeader>
              <CardTitle id="delete-account-heading">Delete account</CardTitle>
              <p className="text-sm text-muted-foreground">
                Your collections and their listings go with it. This can't be undone.
              </p>
            </CardHeader>
            <CardContent>
              <Button variant="destructive" onClick={() => setDeleting(true)}>
                Delete account
              </Button>
            </CardContent>
          </Card>
          <DeleteAccountDialog open={deleting} onOpenChange={setDeleting} />
        </>
      )}
    </div>
  );
}

/** The published pages about the service, each opened in a new tab. */
const LEGAL_LINKS = [
  { label: 'Privacy Policy', href: PRIVACY_URL },
  { label: 'Terms of Use', href: TERMS_URL },
  { label: 'Help & Support', href: SUPPORT_URL },
];

export default AccountSettingsPage;
